import { 
  ClientPerformanceData, 
  ClientPerformanceResponse, 
  ClientPerformanceFilters 
} from '../types';
import { RateLimiter } from '../utils/validation';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class ClientPerformanceApiService {
  private readonly API_URL = 'https://cloudapi.mailercloud.com/v1/clientperform/report';
  private readonly PASSCODE = process.env.REACT_APP_MAILERCLOUD_API_PASSCODE;
  private cache = new Map<string, CacheEntry<any>>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private pendingRequests = new Map<string, Promise<any>>();
  private rateLimiter = new RateLimiter(100, 60000); // 100 requests per minute

  private getCacheKey(filters?: ClientPerformanceFilters): string {
    return JSON.stringify(filters || {});
  }

  private getFromCache(key: string): ClientPerformanceData[] | null {
    const cached = this.cache.get(key);
    if (cached) {
      const now = Date.now();
      if (now - cached.timestamp < cached.ttl) {
        return cached.data;
      } else {
        this.cache.delete(key);
      }
    }
    return null;
  }

  private setCache(key: string, data: ClientPerformanceData[]): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: this.CACHE_TTL
    });
  }

  clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  async fetchPerformanceData(
    filters?: ClientPerformanceFilters
  ): Promise<ClientPerformanceData[]> {
    const cacheKey = this.getCacheKey(filters);

    // Check cache first
    const cachedData = this.getFromCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    // Check for pending request with same parameters
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    // Create new request
    const requestPromise = this.performRequest(filters);
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const data = await requestPromise;
      this.setCache(cacheKey, data);
      return data;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  private async performRequest(
    filters?: ClientPerformanceFilters
  ): Promise<ClientPerformanceData[]> {
    await this.rateLimiter.checkRateLimit();

    if (!this.PASSCODE) {
      throw new Error('MailerCloud API passcode not configured');
    }

    const requestBody: any = {
      passcode: this.PASSCODE
    };

    // Add filters to request body
    if (filters?.dateFrom) {
      requestBody.date_from = filters.dateFrom;
    }
    if (filters?.dateTo) {
      requestBody.date_to = filters.dateTo;
    }

    try {
      const response = await fetch(this.API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const result: ClientPerformanceResponse = await response.json();

      if (result.status !== 'success') {
        throw new Error(result.message || 'API request failed');
      }

      // Process and filter data based on additional filters
      let processedData = result.data || [];

      if (filters?.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        processedData = processedData.filter(item =>
          item.company_name.toLowerCase().includes(searchLower) ||
          item.domain.toLowerCase().includes(searchLower) ||
          item.tags?.toLowerCase().includes(searchLower)
        );
      }

      if (filters?.domain) {
        processedData = processedData.filter(item =>
          item.domain.toLowerCase() === filters.domain!.toLowerCase()
        );
      }

      if (filters?.minInboxRate !== undefined) {
        processedData = processedData.filter(item => {
          const inboxRate = parseFloat(item.overall_inbox_pct.replace('%', ''));
          return !isNaN(inboxRate) && inboxRate >= filters.minInboxRate!;
        });
      }

      if (filters?.maxSpamRate !== undefined) {
        processedData = processedData.filter(item => {
          const spamRate = parseFloat(item.spam_rate.replace('%', ''));
          return !isNaN(spamRate) && spamRate <= filters.maxSpamRate!;
        });
      }

      return processedData;
    } catch (error) {
      console.error('Error fetching client performance data:', error);
      throw error;
    }
  }

  getPerformanceLevel(metric: string, type: 'inbox' | 'spam' | 'open'): 'good' | 'warning' | 'critical' {
    const value = parseFloat(metric.replace('%', ''));
    
    if (isNaN(value)) {
      return 'critical';
    }

    switch (type) {
      case 'inbox':
        if (value >= 90) return 'good';
        if (value >= 70) return 'warning';
        return 'critical';
      
      case 'spam':
        if (value <= 2) return 'good';
        if (value <= 5) return 'warning';
        return 'critical';
      
      case 'open':
        if (value >= 20) return 'good';
        if (value >= 10) return 'warning';
        return 'critical';
      
      default:
        return 'warning';
    }
  }

  getDomainReputationLevel(reputation: string): 'good' | 'warning' | 'critical' {
    const rep = reputation.toLowerCase();
    if (rep.includes('high') || rep.includes('good') || rep.includes('excellent')) {
      return 'good';
    }
    if (rep.includes('medium') || rep.includes('moderate') || rep.includes('fair')) {
      return 'warning';
    }
    return 'critical';
  }

  formatPercentage(value: string | number): string {
    if (typeof value === 'number') {
      return `${value.toFixed(2)}%`;
    }
    // If already has %, return as is
    if (value.includes('%')) {
      return value;
    }
    // Try to parse and format
    const num = parseFloat(value);
    if (!isNaN(num)) {
      return `${num.toFixed(2)}%`;
    }
    return value;
  }

  formatVolume(value: string | number): string {
    const num = typeof value === 'number' ? value : parseInt(value);
    if (isNaN(num)) {
      return value.toString();
    }
    return num.toLocaleString();
  }

  formatDecimal(value: string | number, decimalPlaces: number = 2): string {
    // Handle empty or undefined values
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const num = typeof value === 'number' ? value : parseFloat(value.toString());
    
    // Handle invalid numbers
    if (isNaN(num)) {
      return value.toString();
    }

    // Return formatted decimal with specified decimal places
    return num.toFixed(decimalPlaces);
  }

  exportToCSV(data: ClientPerformanceData[], filename?: string): void {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Date',
      'Company Name',
      'Domain',
      'Total Volume',
      'Gmail',
      'Yahoo',
      'Microsoft',
      'Others',
      'Delivery Time',
      'Open Rate %',
      'Click Rate %',
      'Gmail Inbox %',
      'Yahoo Inbox %',
      'Microsoft Inbox %',
      'Overall Inbox %',
      'Gmail Spam %',
      'Yahoo Spam %',
      'Microsoft Spam %',
      'Spam %',
      'Spam Rate',
      'Domain Reputation',
      'FBL',
      'Authentication',
      'Delivery Errors',
      'Domain Listed',
      'Total IPs',
      'High',
      'Medium',
      'Low',
      'Bad',
      'No Data',
      'Talos',
      'Proof Point',
      'Blacklist',
      'Sender Score',
      'Tags',
      'Deferrals',
      'Soft Bounces',
      'Queue'
    ];

    // Convert data to CSV rows
    const rows = data.map(item => [
      item.date,
      item.company_name,
      item.domain,
      item.total_volume,
      item.gmail,
      item.yahoo,
      item.microsoft,
      item.others,
      item.delivery_time,
      item.or_pct,
      item.cr_pct,
      item.gmail_inbox_pct,
      item.yahoo_inbox_pct,
      item.microsoft_inbox_pct,
      item.overall_inbox_pct,
      item.gmail_spam_pct,
      item.yahoo_spam_pct,
      item.microsoft_spam_pct,
      item.spam_pct,
      item.spam_rate,
      item.domain_reputation,
      item.fbl,
      item.authen,
      item.delivery_errors,
      item.domain_listed,
      item.total_ips,
      item.high,
      item.medium,
      item.low,
      item.bad,
      item.no_data,
      item.talos,
      item.proof_point,
      item.blacklist,
      item.sender_score,
      item.tags,
      item.deferrals,
      item.soft_bounces,
      item.queue
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => 
        row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma
          const cellStr = String(cell || '');
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename || `client-performance-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

const clientPerformanceApi = new ClientPerformanceApiService();
export default clientPerformanceApi;